import { useEffect, useRef, useState, type FC } from 'react';
import { gsap } from 'gsap';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { formatNumber } from '@/lib/formatNumber';
import { useTranslation } from 'react-i18next';
import styles from './AnimatedNumber.module.css';

interface AnimatedNumberProps {
  value: number;
  prefix?: string;
  suffix?: string;
  /** Decimal places. Default 0. */
  decimals?: number;
}

/**
 * Counts up to `value` whenever the closest section or sticky slot
 * gets `data-active="true"`. Triggered by an attribute change on the
 * ancestor — not by IntersectionObserver or ScrollTrigger — so it works
 * correctly under the stacking-cards scroller where sticky positioning
 * confuses traditional viewport-based observers.
 *
 * On mobile (<=720px) where there's no sticky behaviour, every slot is
 * `data-active="true"` so the animation plays once on mount.
 */
export const AnimatedNumber: FC<AnimatedNumberProps> = ({
  value,
  prefix,
  suffix,
  decimals = 0,
}) => {
  const { i18n } = useTranslation();
  const ref = useRef<HTMLSpanElement>(null);
  const [display, setDisplay] = useState('0');
  const { prefersReducedMotion } = useReducedMotion();

  useEffect(() => {
    if (!ref.current) return;
    // StrictMode + HMR safety — guard against onUpdate firing after
    // cleanup, which can push a stale setState into the scheduler
    // queue and trip React's dev-mode "undefined startTime" error.
    let mounted = true;
    const locale = i18n.language;
    const opts: Intl.NumberFormatOptions = { maximumFractionDigits: decimals };

    // Render the final number up front. The tween below is additive — even
    // if no trigger fires (e.g. user landed mid-section, never scrolled),
    // the value is already correct.
    setDisplay(formatNumber(value, locale, opts));

    if (prefersReducedMotion) return;

    const proxy = { v: 0 };
    const tween = gsap.to(proxy, {
      v: value,
      duration: 1.6,
      ease: 'power2.out',
      paused: true,
      onUpdate: () => {
        if (!mounted) return;
        setDisplay(formatNumber(proxy.v, locale, opts));
      },
    });

    const play = (): void => {
      if (!mounted) return;
      proxy.v = 0;
      tween.seek(0);
      tween.restart();
    };

    // Trigger path A: HorizontalPager sets `data-active="true"` on the
    // closest stickySlot wrapper when the slot should play.
    const target: HTMLElement | null = ref.current.closest('[data-active]');

    let teardown: () => void;
    if (target) {
      const observer = new MutationObserver(() => {
        if (target.dataset.active === 'true') play();
      });
      observer.observe(target, {
        attributes: true,
        attributeFilter: ['data-active'],
      });
      if (target.dataset.active === 'true') play();
      teardown = () => observer.disconnect();
    } else {
      // Trigger path B: standalone sections / non-pager contexts.
      const io = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry && entry.isIntersecting) play();
        },
        { threshold: 0.4 },
      );
      io.observe(ref.current);
      teardown = () => io.disconnect();
    }

    return () => {
      mounted = false;
      teardown();
      tween.kill();
    };
  }, [value, decimals, prefersReducedMotion, i18n.language]);

  return (
    <span ref={ref} className={styles.root}>
      {prefix && <span className={styles.affix}>{prefix}</span>}
      <span className={styles.value}>{display}</span>
      {suffix && <span className={styles.affix}>{suffix}</span>}
    </span>
  );
};

AnimatedNumber.displayName = 'AnimatedNumber';
